'use client'

import Link from 'next/link'
import { useEffect, useRef } from 'react'
import { track } from '@/utils/analytics'
import type { BlogPostMeta } from '@/app/blog/posts'

/**
 * Two "read next" links placed inside the article, above the point where most
 * readers leave.
 *
 * PostHog, 26 Aug – 13 Sep 2026: 40% of article views reach 25% scroll, 19%
 * reach 50%, 6% reach 75%. The Related Research grid sits below the article,
 * so it is seen by a small minority, and related-article clicks were 0.4% of
 * views with ~1.2 pages per session. This puts the next step where readers
 * still are. It is placed by BlogPostLayout after the mid-article CTA, so it
 * never competes with that placement.
 */
export default function ReadNextInline({
  posts,
  hub,
  postSlug,
  postTopic,
}: {
  posts: BlogPostMeta[]
  /** The article's topic hub, from BlogPostLayout's TOPIC_HUB map. */
  hub?: { href: string; label: string }
  postSlug: string
  postTopic: string
}) {
  const ref = useRef<HTMLElement | null>(null)
  const firedRef = useRef(false)

  // Impression on first scroll-into-view, not on mount, so the click rate is
  // measured against readers who actually reached the block.
  useEffect(() => {
    const el = ref.current
    if (!el || firedRef.current) return
    const fire = () => {
      if (firedRef.current) return
      firedRef.current = true
      track('read_next_impression', {
        cta_surface: 'blog_read_next',
        cta_position: 'mid_article',
        page_type: 'blog',
        post_slug: postSlug,
        post_topic: postTopic,
      })
    }
    if (typeof IntersectionObserver === 'undefined') {
      fire()
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          fire()
          observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [postSlug, postTopic])

  if (posts.length === 0) return null

  return (
    <aside ref={ref} className="not-prose my-8 border-l-4 border-forest/40 pl-4" aria-label="Read next">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-forest mb-2">Read next</p>
      <ul className="space-y-2">
        {posts.map((post) => (
          <li key={post.slug}>
            <Link
              href={`/blog/${post.slug}`}
              onClick={() =>
                track('related_article_click', {
                  cta_surface: 'blog_read_next',
                  cta_position: 'mid_article',
                  post_slug: postSlug,
                  post_topic: postTopic,
                  destination: `/blog/${post.slug}`,
                  destination_topic: post.topic,
                })
              }
              className="font-semibold text-ink underline decoration-forest/30 underline-offset-4 hover:decoration-forest"
            >
              {post.title}
            </Link>
          </li>
        ))}
      </ul>
      {hub ? (
        <p className="mt-2 text-sm text-gray-600">
          Or see the whole topic:{' '}
          <Link
            href={hub.href}
            onClick={() =>
              track('topic_hub_click', {
                cta_surface: 'blog_read_next',
                cta_position: 'mid_article',
                post_slug: postSlug,
                post_topic: postTopic,
                destination: hub.href,
              })
            }
            className="font-medium text-forest underline"
          >
            {hub.label}
          </Link>
        </p>
      ) : null}
    </aside>
  )
}
